import { format, parseISO } from 'date-fns'
import { MaterialReactTable } from 'material-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { PageWrap } from '@/components'
import { useTable } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { useGetStructureHistoryQuery } from '@/rtkApi'
import { HistoryWithDeploymentDto } from '@/rtkApi/mddApiSliceGeneratedV2'

import { VersionDiffModal } from './components/VersionDiffModal'
import { useVersionsTableColumns } from './hooks'

const pickLatestHistory = (
	items: HistoryWithDeploymentDto[] | undefined,
): HistoryWithDeploymentDto | null => {
	if (!items?.length) return null
	return items.reduce((best, item) =>
		(item.version ?? 0) > (best.version ?? 0) ? item : best,
	)
}

export const NewVersionsTab = () => {
	const {
		state: { editMode, node },
	} = useDetailTabContext()
	const branchId = useSelectedBranchNumericId()

	const [diffRow, setDiffRow] = useState<HistoryWithDeploymentDto | null>(null)

	const {
		data,
		isLoading,
		refetch,
	} = useGetStructureHistoryQuery(
		{
			branchId: branchId ?? 0,
			structureId: node.id,
			pageNumber: 1,
			pageSize: 200,
		},
		{ skip: branchId == null || !node.id },
	)
	const tableData = data?.content

	useEffect(() => {
		refetch()
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refetch omitted to avoid loop when query ref identity changes
	}, [editMode])

	const dataWithParsedDate = tableData?.map((item) => {
		const parsedDate = parseISO(item.created || '')
		return {
			...item,
			created: format(parsedDate, 'dd.MM.yyyy HH:mm'),
		}
	})

	const latestHistory = useMemo(
		() => pickLatestHistory(tableData),
		[tableData],
	)

	const onOpenDiff = useCallback((history: HistoryWithDeploymentDto) => {
		setDiffRow(history)
	}, [])

	const columns = useVersionsTableColumns({ onOpenDiff })

	const table = useTable<HistoryWithDeploymentDto>({
		columns,
		data: dataWithParsedDate || [],
		editMode,
		options: {
			enableEditing: true,
			editDisplayMode: 'table',
			enableRowActions: false,
			enableRowOrdering: false,
			state: {
				isLoading,
			},
			enableTopToolbar: true,
			enableGlobalFilter: true,
		},
	})

	return (
		<PageWrap>
			<MaterialReactTable table={table} />
			{diffRow != null && latestHistory != null && (
				<VersionDiffModal
					structureId={node.id}
					structureName={node.name ?? node.code}
					leftHistoryId={diffRow.id}
					leftVersion={diffRow.version}
					rightHistoryId={latestHistory.id}
					rightVersion={latestHistory.version}
					onClose={() => setDiffRow(null)}
				/>
			)}
		</PageWrap>
	)
}
